import { HttpClient } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    BehaviorSubject,
    catchError,
    first,
    map,
    mergeMap,
    Observable,
    of,
    Subject,
    takeUntil,
    zip
} from 'rxjs';
import { environment as env } from '../../../environments/environment';
import {
    LgvAPIService,
    LgvTSHelper,
    LgvUserService,
    NotificationService
} from '../../core/core.module';
import { LgvModel } from '../../data-models/longevity.model';
import { ImageUploaderModalComponent } from '../image-uploader-modal/image-uploader-modal.component';
import { SocialMediaEditModalComponent } from '../social-media-edit-modal/social-media-edit-modal.component';
import { Router } from '@angular/router';
import { BillingModalComponent } from '../billing-modal/billing-modal.component';
import { UserEditModalComponent } from '../user-edit-modal/user-edit-modal.component';
import { DatePipe } from '@angular/common';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { LgvCompanyService } from '../../core/services/lgv-company.service';
import { UserInviteModalComponent } from '../user-invite-modal/user-invite-modal.component';
import { PricingPreferencesModalComponent } from '../pricing-preferences-modal/pricing-preferences-modal.component';
import { MatRadioChange } from '@angular/material/radio';
import { FormControl } from '@angular/forms';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { EditPpeOriginationIDComponent } from '../edit-ppe-origination-id/edit-ppe-origination-id.component';
import { RFM } from '../../modules/default/default.enum';
import { EditUserConfigModalComponent } from '../edit-user-config-modal/edit-user-config-modal.component';
import { response } from 'express';
import { LgvPricingService } from '../../features/pricing/services/pricing.service';
import { SafeUrl } from '@angular/platform-browser';
import {
    LgvUserStateLicensingService,
    UserStateLicense
} from '../../core/services/lgv-user-licensing.service';
import { LicenseEditModalComponent } from '../license-edit-modal/license-edit-modal.component';
import { LgvStatesHelper } from '../../core/utility/lgv-states-helper';

@Component({
    selector: 'lgv-user-profile2',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserProfileComponent implements OnInit, OnChanges, OnDestroy {
    @Input() userId: string = null;
    onDestroy$ = new Subject();

    _agent: LgvModel.Agent = null;
    agent$ = new BehaviorSubject(this._agent);

    _agentCompany: LgvModel.CompanyInfo = null;
    agentCompany$ = new BehaviorSubject(this._agentCompany);

    isLoading$ = new BehaviorSubject(false);
    agentImgUrl$ = new BehaviorSubject(null);

    _billingStatus: BillingStatus = BillingStatus.Loading;
    billingStatus$ = new BehaviorSubject(this._billingStatus);

    _coBrandingAgents: LgvModel.Agent[] = null;
    coBrandingAgents$ = new BehaviorSubject(this._coBrandingAgents);

    _pricingApplicationCompany: LgvModel.ApplicationCompany = null;
    pricingApplicationCompany$ = new BehaviorSubject(
        this._pricingApplicationCompany
    );

    _lpApplicationUser: LgvModel.ApplicationUser = null;
    lpApplicationUser$ = new BehaviorSubject(this._lpApplicationUser);

    _posApplicationCompany: LgvModel.ApplicationCompany = null;
    posApplicationCompany$ = new BehaviorSubject(this._posApplicationCompany);

    floifyToken$ = new BehaviorSubject(null);
    mloShortCode$ = new BehaviorSubject(null);

    assistingAgents$ = new BehaviorSubject(null);
    assistants$ = new BehaviorSubject(null);

    assignedCkUser$ = new BehaviorSubject<LgvModel.UserDTO>(null);
    selectedCkUser$ = new BehaviorSubject<LgvModel.UserDTO>(null);
    ckSearchState: string;

    ppeProviderCode$ = new BehaviorSubject(null);
    newAssistantBindingType = 'Assistant';
    assistantGetsEmails = false;
    childSearchState: string;
    _childSearchState: string;
    selectedAssistantBindingUser: LgvModel.UserDTO;
    selectedCoBrandingUser: LgvModel.UserDTO;
    invitationUrl = '';
    myAngularxQrCode: string = '';
    qrCodeDownloadLink: SafeUrl = '';
    darkColor = '#4A2F89';
    lightColor = '#F7F9F7';
    LgvClientSvc: any;

    _userLicenses: UserStateLicense[] = [];
    userLicenses$ = new BehaviorSubject<UserStateLicense[]>([]);

    get agentInfo(): LgvModel.Agent {
        return !!this.agent$.value ? this.agent$.value : null;
    }
    get csrRelativeLink(): string {
        return `/csr/${this.userId}`;
    }
    get qpRelativeLink(): string {
        return `/mlo-csr/${this.userId}`;
    }
    get rsrRelativeLink(): string {
        return `/rsr/${this.userId}`;
    }
    get mloClientRegistrationRelativeLink(): string {
        return `mlo-client-registration/${this.userId}`;
    }

    get isProd(): boolean {
        return env.envName === 'PROD' || env.envName === 'UAT';
    }

    get clientSelfRegistrationLink(): string {
        return `${LgvTSHelper.environmentBaseUrl}#${this.csrRelativeLink}?c=${this.agentCompany$.value.companyCode}`;
    }

    get qpClientSelfRegistrationLink(): string {
        return `${LgvTSHelper.environmentBaseUrl}#${this.qpRelativeLink}?c=${this.agentCompany$.value.companyCode}`;
    }

    get realtorSelfRegistrationLink(): string {
        return `${LgvTSHelper.environmentBaseUrl}#${this.rsrRelativeLink}?c=${this.agentCompany$.value.companyCode}`;
    }

    get fullMloQpShortUrl(): string {
        return `${LgvTSHelper.environmentBaseUrl}#/mlo/${this.mloShortCode$.value}`;
    }

    get baseUrl(): string {
        const curUrlParts = window.location.href.split('#');
        if (curUrlParts.length != 2) {
            return;
        }
        return `${curUrlParts[0]}`;
    }

    get agentIsLive(): boolean {
        return this.agentInfo?.enrollmentDate != null;
    }

    invitationJustSent$ = new BehaviorSubject(false);

    get canSendNewInvitation$(): Observable<boolean> {
        return this.lgvUserSvc.Agent$.pipe(
            map(x => {
                if (x == null || x.loinvitationSentDate === null) {
                    return true;
                } else {
                    const dNow = new Date();
                    const dInvitationSent = LgvTSHelper.jsonDateToJSDate(
                        x.loinvitationSentDate
                    );
                    const hoursSinceLastSend =
                        (dNow.getTime() - dInvitationSent.getTime()) / 3600000;
                    return hoursSinceLastSend > 24;
                }
            })
        );
    }

    pricingSettings = null;

    constructor(
        private http: HttpClient,
        public lgvUserSvc: LgvUserService,
        private lgvCompanySvc: LgvCompanyService,
        private lgvApiSvc: LgvAPIService,
        private lgvPricingSvc: LgvPricingService,
        private notificationSvc: NotificationService,
        private router: Router,
        private datePipe: DatePipe,
        public matDialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private userLicensingService: LgvUserStateLicensingService,
        private lgvStatesHelper: LgvStatesHelper
    ) {}

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges) {
        if (!!changes.userId && !!changes.userId.currentValue) {
            //console.log('Change: ' + changes.userId.currentValue);

            this.loadAgentInfo();
            this.loadCoBrandingAgents();
            this.loadAgentBinding();
            this.loadUserLicenses();
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    refreshAgentData() {
        if (this.lgvUserSvc.IsImpersonatingAgent) {
            this.lgvUserSvc.ImpersonateAgent(this.userId, true);
        } else {
            this.lgvUserSvc.LoadDBUserSS(false);
            this.loadAgentInfo();
        }
    }

    onEditAgentImage() {
        const dialogRef = this.matDialog.open(ImageUploaderModalComponent, {
            width: '400px',

            data: {
                hdImage: {
                    key: 'User_Profile',
                    url$: this.agentImgUrl$,
                    targetHeight: null,
                    maxHeight: 150,
                    aspectRatio: 3 / 4,
                    title: 'User Profile Image',
                    darkBkg: false
                },

                userId: this.userId
                //    currentImgUrl: this.AgentModel$.value?.profileImageUrl,
                //    hdImgType: HDImageType.User_Profile
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    this.refreshAgentData();
                }
            });
    }

    onEditSocialMedia() {
        const dialogRef = this.matDialog.open(SocialMediaEditModalComponent, {
            width: '550px',
            disableClose: true,
            data: { agentInfo: this.agent$.value }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    this.refreshAgentData();
                }
            });
    }

    onClickCopyCSR() {
        navigator.clipboard.writeText(this.clientSelfRegistrationLink);
        this.notificationSvc.success('Link copied!');
    }

    onPreviewCSR() {
        window.open(`#/${this.csrRelativeLink}`, '_blank');
    }

    onClickCopyRSR() {
        navigator.clipboard.writeText(this.realtorSelfRegistrationLink);
        this.notificationSvc.success('Link copied!');
    }

    onPreviewRSR() {
        window.open(`#/${this.rsrRelativeLink}`, '_blank');
    }

    onClickCopyQP() {
        if (this.mloShortCode$.value !== null) {
            navigator.clipboard.writeText(this.fullMloQpShortUrl);
        } else {
            navigator.clipboard.writeText(this.qpClientSelfRegistrationLink);
        }
        this.notificationSvc.success('Link copied!');
    }

    onPreviewQP() {
        window.open(`#/${this.qpRelativeLink}`, '_blank');
    }

    onResetPassword() {
        this.lgvUserSvc
            .PasswordReset(this.lgvUserSvc.AgentID)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                res => {
                    this.notificationSvc.success('Reset Password email sent');
                },
                () => {
                    this.notificationSvc.error(
                        'There was a problem sending the reset password email.'
                    );
                }
            );
    }

    onCreateBillingAccount() {
        const agent = this.agent$.value;
        const company = this.agentCompany$.value;

        this.lgvUserSvc
            .createBillingAccount(agent, company, this.userId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                response => {
                    this.notificationSvc.success(
                        'Billing account has been created'
                    );
                    this.onLaunchBillingModal('manage-payment-sources');
                    this.billingStatus$.next(BillingStatus.NoPaymentMethod);
                },
                err =>
                    this.notificationSvc.error(
                        'Unable to create billing account'
                    )
            );
    }

    onLaunchBillingModal(modalType: string) {
        this.isLoading$.next(true);

        this.lgvUserSvc
            .startBillingPortalSession(this.userId, modalType)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                portalSessionResponse => {
                    this.isLoading$.next(false);
                    const dialogRef = this.matDialog.open(
                        BillingModalComponent,
                        {
                            width: '400px',
                            panelClass: 'billing-modal',
                            disableClose: true,
                            data: {
                                userInfo: this.agentInfo,
                                iFrameUrl: portalSessionResponse.url
                            }
                        }
                    );
                    dialogRef
                        .afterClosed()
                        .pipe(takeUntil(this.onDestroy$))
                        .subscribe(x => {
                            this.onUpdateBillingStatus();
                        });
                },
                err => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error(
                        'Unable to start portal session with Billing Provider'
                    );
                }
            );
    }

    onUpdateBillingStatus() {
        this.lgvUserSvc
            .updateBillingStatus(this.userId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                x => {
                    //console.log('billingInfo:');
                    //console.log(x);

                    let cardStatus: string = x?.customer?.cardStatus;
                    this.billingStatus$.next(
                        !!cardStatus && cardStatus === 'valid'
                            ? BillingStatus.Good
                            : BillingStatus.NoPaymentMethod
                    );
                },
                err => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error(
                        'There was an error loading the billing information'
                    );
                    console.log('Error:');
                    console.log(err);
                }
            );
    }

    onEditUser() {
        const dialogRef = this.matDialog.open(UserEditModalComponent, {
            width: '550px',
            panelClass: 'dialog-container',
            disableClose: true,
            data: {
                userInfo: this.agentInfo,
                companyId: this.agentInfo?.companyId
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    this.refreshAgentData();
                }
            });
    }

    onEditBillingPreferences() {
        const dialogRef = this.matDialog.open(
            PricingPreferencesModalComponent,
            {
                width: '550px',
                panelClass: 'dialog-container',
                disableClose: true,
                data: {
                    agentInfo: this.agentInfo
                }
            }
        );

        //    dialogRef.afterClosed().pipe(takeUntil(this.onDestroy$)).subscribe(x => {
        //        if (x.action === true) {
        //            this.refreshAgentData();
        //        }
        //    });
    }

    onEditPPEOriginationID() {
        const dialogRef = this.matDialog.open(EditPpeOriginationIDComponent, {
            width: '550px',
            panelClass: 'dialog-container',
            disableClose: true,
            data: {
                agentInfo: this.agentInfo,
                lpApplicationUser: this.lpApplicationUser$.value,
                pricingApplicationCompany: this.pricingApplicationCompany$.value
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    this.refreshAgentData();
                }
            });
    }

    onEditFloifyToken() {
        const dialogRef = this.matDialog.open(EditUserConfigModalComponent, {
            width: '550px',
            panelClass: 'dialog-container',
            disableClose: true,
            data: {
                entityKey: EditUserConfigModalComponent.floifyTokenKey,
                entityValue: this.floifyToken$.value,
                agentInfo: this.agentInfo,
                lpApplicationUser: this.lpApplicationUser$.value,
                pricingApplicationCompany: this.pricingApplicationCompany$.value
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    this.refreshAgentData();
                }
            });
    }

    onInviteRealEstateAgent() {
        const dialogRef = this.matDialog.open(UserInviteModalComponent, {
            width: '550px'
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    const requestBody = {
                        assignedCompanyId: this.agent$.value.companyId,
                        firstName: x.formValue.firstName,
                        lastName: x.formValue.lastName,
                        email: x.formValue.email
                    } as LgvModel.UserInviteModel;
                    this.lgvApiSvc
                        .sendUserInvite(this.userId, requestBody)
                        .pipe(
                            catchError(error => {
                                this.notificationSvc.error(
                                    error?.error?.detail
                                );
                                return of(false);
                            })
                        )
                        .subscribe(invite => {
                            if (invite) {
                                this.notificationSvc.success(
                                    'Invite email sent.'
                                );
                            }
                        });
                }
            });
    }

    onToggleGoLive(args: MatSlideToggleChange) {
        const today = this.datePipe.transform(
            new Date().getTime(),
            'yyyy-MM-ddThh:mm:ss'
        );

        this.isLoading$.next(true);
        const model = {
            firstName: this.agentInfo.firstName,
            lastName: this.agentInfo.lastName,
            jobTitle: this.agentInfo.jobTitle,
            goLiveWithEmailCampaign: args.checked ? true : false
        };

        const bldUrl = `${env.LgvApiBaseUrl}/users/${this.userId}`;

        this.http
            .patch<any>(bldUrl, model)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                x => {
                    this.isLoading$.next(false);
                    this.notificationSvc.success('Profile has been updated');
                    //this.refreshAgentData();
                },
                err => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error(
                        `There was an error updating the profile. Please edit the agent's profile and make sure to fill out the required fields first.`
                    );
                    console.log('Error:');
                    console.log(err);
                }
            );
    }

    loadAgentInfo() {
        this.http
            .get<LgvModel.Agent>(`${env.LgvApiBaseUrl}/users/${this.userId}/`)
            .pipe(
                takeUntil(this.onDestroy$),
                mergeMap(agentInfo => {
                    return zip(
                        of(agentInfo),
                        this.lgvCompanySvc
                            .getById(agentInfo.companyId)
                            .pipe(takeUntil(this.onDestroy$)),
                        this.lgvUserSvc.fetchPricingSettings(agentInfo.id)
                    );
                })
            )
            .subscribe(
                ([agentInfo, companyInfo, pricingSettings]) => {
                    this.pricingSettings = pricingSettings;
                    // place a unique suffix on the agent photo so it is never cached for admin view
                    const url = agentInfo.profileImageUrl;
                    if (url && url.length > 0 && url.endsWith('User_Profile')) {
                        this.agentImgUrl$.next(
                            agentInfo.profileImageUrl + LgvTSHelper.imgSuffix()
                        );
                    } else {
                        this.agentImgUrl$.next(null);
                    }

                    this.agent$.next(agentInfo);
                    this.agentCompany$.next(companyInfo);

                    this.invitationUrl = `${this.baseUrl}#/token/agent/${agentInfo.id}`;

                    // Auto-generate short URL if it doesn't exist yet
                    if (this.mloShortCode$.value === null) {
                        this.shortenUrl();
                    }

                    if (!!agentInfo.billingEntityId) {
                        this.onUpdateBillingStatus();
                    } else {
                        this.billingStatus$.next(BillingStatus.NoAccount);
                    }

                    let lpUserApp = agentInfo.userApplications.find(
                        f =>
                            f.application.type === 'Pricing' &&
                            f.application.name === 'LenderPrice'
                    );
                    if (!!lpUserApp) {
                        this.lpApplicationUser$.next(lpUserApp);
                    }

                    const longUrlUserApp = agentInfo?.userApplications?.find(
                        ua =>
                            ua.application?.type === 'LongUrl' &&
                            ua.indexName === 'MloLongUrlCode'
                    );
                    if (!!longUrlUserApp) {
                        this.mloShortCode$.next(longUrlUserApp.indexValue);
                    }

                    this.lgvPricingSvc
                        .getCompanyPricingEnginePublic(companyInfo.companyCode)
                        .pipe(first(), takeUntil(this.onDestroy$))
                        .subscribe(ac => {
                            switch (ac?.name) {
                                case 'OptimalBlue':
                                    this.ppeProviderCode$.next('OB');
                                    break;
                                case 'LenderPrice':
                                    this.ppeProviderCode$.next('LP');
                                    break;
                            }
                            this.pricingApplicationCompany$.next(ac);
                        });

                    this.lgvCompanySvc
                        .getPOSCompanyPublic(companyInfo.companyCode)
                        .pipe(first(), takeUntil(this.onDestroy$))
                        .subscribe(ac => {
                            //console.log('Result from getPOSCompanyPublic:');
                            //console.log(ac);
                            this.posApplicationCompany$.next(ac);
                        });

                    this.lgvUserSvc
                        .getUserFloifyToken(this.userId)
                        .pipe(first(), takeUntil(this.onDestroy$))
                        .subscribe(x => {
                            //console.log('Response from get Floify Token:');
                            //console.log(x);
                            this.floifyToken$.next(x.value);
                        });

                    if (agentInfo?.ckUserId) {
                        this.loadCKUserInfo(agentInfo.ckUserId);
                    }
                },
                err => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error(
                        'There was an error loading the agent information'
                    );
                    console.log('Error:');
                    console.log(err);
                }
            );
    }

    loadCoBrandingAgents() {
        this.http
            .get<any>(
                `${env.LgvApiBaseUrl}/users/${this.userId}/cobranding/realestate-agents?&top=100`
            )
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                x => {
                    console.log('coBrandingAgents:');
                    console.log(x);
                    this.coBrandingAgents$.next(x.value);
                },
                err => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error(
                        'There was an error loading the co-branded agents'
                    );
                    console.log('Error:');
                    console.log(err);
                }
            );
    }

    //#region CK Assignments
    private loadCKUserInfo(ckUserId: string) {
        this.lgvUserSvc
            .GetUserById(ckUserId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                ckUser => {
                    if (!!ckUser) {
                        this.assignedCkUser$.next(ckUser);
                    }
                },
                () => {
                    this.assignedCkUser$.next(null);
                }
            );
    }

    onCKSelection(user: LgvModel.UserDTO) {
        this.selectedCkUser$.next(user);
    }

    onChangeCKUser() {
        let ckUserId = this?.selectedCkUser$?.value?.id ?? null;

        this.isLoading$.next(true);

        const model = {
            firstName: this.agentInfo.firstName,
            lastName: this.agentInfo.lastName,
            jobTitle: this.agentInfo.jobTitle,
            cKUserId: ckUserId
        };

        const bldUrl = `${env.LgvApiBaseUrl}/users/${this.userId}`;

        this.http
            .patch<any>(bldUrl, model)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                () => {
                    this.isLoading$.next(false);
                    if (!!ckUserId) {
                        this.notificationSvc.success(
                            'CK assigned successfully.'
                        );
                        this.assignedCkUser$.next(this?.selectedCkUser$?.value);
                    } else {
                        this.notificationSvc.success(
                            'CK removed successfully.'
                        );
                    }
                    this.selectedCkUser$.next(null);
                },
                err => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error('Error assigning CK user.');
                    console.log('Error:', err);
                }
            );
    }
    //#endregion CK Assignment

    loadAgentBinding() {
        this.lgvUserSvc
            .FetchAssistantToBinding(this.userId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                this.assistingAgents$.next(x);
            });
        this.lgvUserSvc
            .FetchAssistantsBinding(this.userId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                this.assistants$.next(x);
            });
    }

    onRemoveAssistant(assistantID: string) {
        let agentID = this.userId;
        this.lgvUserSvc
            .RemoveAssistantBinding(assistantID, agentID)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                () => {
                    this.notificationSvc.success('Assistant Removed');
                    this.loadAgentBinding();
                    this.reloadCurrentUserBindingsIfNecessary(
                        this.userId,
                        assistantID
                    );
                },
                () => {
                    this.notificationSvc.error(
                        'There was a error removing the assistant.'
                    );
                }
            );
    }

    onRemoveAssistingAgent(assistantID: string) {
        this.lgvUserSvc
            .RemoveAssistantBinding(this.userId, assistantID)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                () => {
                    this.notificationSvc.success('Assisting Agent Removed');
                    this.loadAgentBinding();
                    this.reloadCurrentUserBindingsIfNecessary(
                        this.userId,
                        assistantID
                    );
                },
                () => {
                    this.notificationSvc.error(
                        'There was a error removing the assistant.'
                    );
                }
            );
    }

    onNewBindingIsAgentChanged(event: MatRadioChange) {
        this.newAssistantBindingType = event.value;
    }

    onToggleNewAssistantGetsEmails(args: MatSlideToggleChange) {
        this.assistantGetsEmails = args.checked;
    }

    reloadCurrentUserBindingsIfNecessary(userID1: string, userID2: string) {
        // If one of the two users were the actual dbUser, need to force bindings to update so the "Impersonate" menu is refreshed
        const dbUserID = this.lgvUserSvc.AgentID;

        if (userID1 === dbUserID || userID2 == dbUserID) {
            this.lgvUserSvc.LoadDBUserAssistantBinding(dbUserID);
        }
    }

    onUserSelectionMade(user: LgvModel.UserDTO) {
        this.childSearchState = null;
        this.selectedAssistantBindingUser = user;
    }

    onUserCoBrandingSelectionMade(user: LgvModel.UserDTO) {
        this._childSearchState = null;
        this.selectedCoBrandingUser = user;
    }

    onAddAssistantBinding() {
        this.isLoading$.next(true);
        const arg1 =
            this.newAssistantBindingType === 'Assistant'
                ? this.selectedAssistantBindingUser.id
                : this.userId;
        const arg2 =
            this.newAssistantBindingType !== 'Assistant'
                ? this.selectedAssistantBindingUser.id
                : this.userId;

        this.lgvUserSvc
            .AddAssistantBinding(arg1, arg2, this.assistantGetsEmails)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                () => {
                    this.isLoading$.next(false);
                    this.notificationSvc.success('Assistant Binding Added');
                    this.loadAgentBinding();
                    this.reloadCurrentUserBindingsIfNecessary(arg1, arg2);
                    this.selectedAssistantBindingUser = null;
                    this.childSearchState = 'Reset';
                },
                () => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error(
                        'There was an error adding the assistant.'
                    );
                }
            );
    }

    onTogglePricing(args: MatSlideToggleChange) {
        const today = this.datePipe.transform(
            new Date().getTime(),
            'yyyy-MM-ddThh:mm:ss'
        );

        this.isLoading$.next(true);
        const model = {
            firstName: this.agentInfo.firstName,
            lastName: this.agentInfo.lastName,
            jobTitle: this.agentInfo.jobTitle,
            hidePricingForClientView: args.checked ? true : false
        };

        const bldUrl = `${env.LgvApiBaseUrl}/users/${this.userId}`;

        this.http
            .patch<any>(bldUrl, model)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(
                x => {
                    this.isLoading$.next(false);
                    this.notificationSvc.success('Profile has been updated');
                },
                err => {
                    this.isLoading$.next(false);
                    this.notificationSvc.error(
                        `There was an error updating the profile. Please edit the agent's profile and make sure to fill out the required fields first.`
                    );
                    console.log('Error:');
                    console.log(err);
                }
            );
    }

    onConfirmSendInvitationEmail() {
        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            width: '250px',
            data: {
                Confirmation: `Are you sure you want to send this invitation?`
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    this.invitationJustSent$.next(true);
                    this.lgvApiSvc
                        .SendInvitationEmail(this.lgvUserSvc.AgentID)
                        .pipe(takeUntil(this.onDestroy$))
                        .subscribe(
                            () => {
                                this.notificationSvc.success(
                                    'Invitation email sent'
                                );
                                this.agentInfo.loinvitationSentDate = LgvTSHelper.dateLocalToUTC(
                                    new Date()
                                );
                            },
                            () => {
                                this.notificationSvc.error(
                                    'There was a problem sending the invitation email.'
                                );
                            }
                        );
                }
            });
    }

    getSocialMediaTypeName(id): any[] {
        return this.lgvApiSvc.socialMediaTypes$.value?.find(
            sm => sm?.id.toUpperCase() === id.toUpperCase()
        )?.name;
    }

    shortenUrl() {
        this.lgvUserSvc
            .saveLongUrl(this.qpClientSelfRegistrationLink)
            .pipe(first())
            .subscribe(response => {
                this.mloShortCode$.next(response.code);
            });
    }

    onQrCodeUrlChange(url: SafeUrl) {
        this.qrCodeDownloadLink = url;
    }

    onToggleDark() {
        const oldDark = this.darkColor;
        this.darkColor = this.lightColor;
        this.lightColor = oldDark;
    }

    onConfirmDeleteUser() {
        this.isLoading$.next(true);
        let agent = this.agent$.value;
        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            width: '250px',
            data: {
                Confirmation: `Are you sure you want to ${
                    agent.isDisabled ? 'enable' : 'disable'
                } <b>${agent.firstName} ${agent.lastName}</b>?`
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(x => {
                if (x.action === true) {
                    if (!agent.isDisabled) {
                        this.lgvUserSvc.DeleteUser(agent.id).subscribe(
                            x => {
                                this.notificationSvc.success(
                                    `User ${agent.firstName} ${agent.lastName} has been disabled.`
                                );
                            },
                            err => {
                                this.notificationSvc.error(
                                    `There was an error disabling user ${agent.firstName} ${agent.lastName}.\n${err}`
                                );
                            }
                        );
                    } else {
                        const model = {
                            firstName: this.agentInfo.firstName,
                            lastName: this.agentInfo.lastName,
                            jobTitle: this.agentInfo.jobTitle,
                            isDisabled: false
                        };

                        const bldUrl = `${env.LgvApiBaseUrl}/users/${this.userId}`;

                        this.http
                            .patch<any>(bldUrl, model)
                            .pipe(takeUntil(this.onDestroy$))
                            .subscribe(
                                x => {
                                    this.isLoading$.next(false);
                                    this.notificationSvc.success(
                                        `User ${agent.firstName} ${agent.lastName} has been enabled.`
                                    );
                                },
                                err => {
                                    this.isLoading$.next(false);
                                    this.notificationSvc.error(
                                        `There was an error enabling user ${agent.firstName} ${agent.lastName}.\n${err}`
                                    );
                                }
                            );
                    }
                    this.agent$.value.isDisabled = !agent.isDisabled;
                    this._agent = { ...this.agent$.value };
                    this._agent.isDisabled = !agent.isDisabled;
                    this.agent$.next(this._agent);
                    this.isLoading$.next(false);
                }
            });
    }

    loadUserLicenses() {
        if (!this.userId) return;

        this.userLicensingService.getUserLicenses(this.userId).subscribe({
            next: licenses => {
                this._userLicenses = licenses || [];
                this.userLicenses$.next(this._userLicenses);
            },
            error: error => {
                console.error('Error loading user licenses:', error);
                this.notificationSvc.error(
                    'There was an error loading the user licenses'
                );
                this._userLicenses = [];
                this.userLicenses$.next(this._userLicenses);
            }
        });
    }

    onEditLicense(license?: UserStateLicense) {
        const dialogRef = this.matDialog.open(LicenseEditModalComponent, {
            width: '550px',
            data: {
                entityId: this.userId,
                license: license,
                entityType: 'user'
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(result => {
                if (result?.action === true) {
                    this.loadUserLicenses();
                }
            });
    }

    onDeleteLicense(license: UserStateLicense) {
        const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
            width: '250px',
            data: {
                Confirmation: `Are you sure you want to delete this license?`
            }
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(result => {
                if (result?.action === true) {
                    this.userLicensingService
                        .deleteStateLicense(this.userId, license.id)
                        .subscribe({
                            next: () => {
                                this.notificationSvc.success(
                                    'License has been deleted'
                                );
                                this.loadUserLicenses();
                            },
                            error: () => {
                                this.notificationSvc.error(
                                    'There was an error deleting the license'
                                );
                            }
                        });
                }
            });
    }

    getStateAbbreviation(fullName: string): string {
        return LgvStatesHelper.getStateAbbreviation(fullName);
    }

    getStateName(abbreviation: string): string {
        return LgvStatesHelper.getStateName(abbreviation);
    }
}

export enum BillingStatus {
    Loading = 'Loading',
    NoAccount = 'NoAccount',
    NoPaymentMethod = 'NoPaymentMethod',
    Good = 'Good'
}
